// Lecture des exports de ventes et agregation par (date, pole).
// Deux formats sont acceptes :
// - "booker"  : rapport "Orders Report" de Spa Booker (jusqu'a fin 2026)
// - "spa"     : rapport "Detail des commandes" de l'app spa (a partir de janvier 2027)
// Chaque lecteur produit des lignes normalisees :
//   { date, pole, subtotal, accueil, superviseur, gratuitType }
// puis agreger() fait le meme travail pour les deux formats.
// Aucun appel reseau ici : tout est testable en isole.

export const CATEGORIES_SPA = ['massages', 'facials']
export const CATEGORIE_HYDRO = 'hydrotherapy'

// Types de l'app spa qui sont des ventes de titres, pas des soins : ignores sans alerte.
const TYPES_VENTE_IGNORES = ['abonnement', 'carte cadeau']

export function parseDate(str) {
  // Format JJ.MM.AAAA
  const [j, m, a] = (str || '').trim().split('.')
  if (!j || !m || !a) return null
  return `${a}-${m.padStart(2, '0')}-${j.padStart(2, '0')}`
}

export function parseMontant(str) {
  if (!str) return 0
  return parseFloat(str.replace(/[€\s]/g, '').replace(',', '.')) || 0
}

export function normaliserNom(str) {
  return (str || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

// Majuscules sans accents, pour comparer libelles de tarifs et categories CSV
export function normaliserLabel(str) {
  return (str || '').trim().toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

// Mots-cles acceptes dans le libelle d'un tarif gratuit, par type detecte
export const MOTS_CLES_GRATUIT = {
  VIP: ['VIP', 'CAMPUS', 'STAFF'],
  ELEVE: ['ELEVE'],
  SUP: ['SUP', 'SUPERVISEUR', 'DEPANNAGE'],
  JPO: ['JPO', 'PORTE'],
}

export function matchSuperviseur(nomCsv, superviseurs) {
  const n = normaliserNom(nomCsv)
  // Correspondance exacte d'abord
  let found = superviseurs.find(s => normaliserNom(`${s.prenom} ${s.nom}`) === n)
  if (found) return found
  // Correspondance sur le prénom seul
  found = superviseurs.find(s => n.includes(normaliserNom(s.prenom)))
  if (found) return found
  // Correspondance sur le nom seul
  found = superviseurs.find(s => n.includes(normaliserNom(s.nom)))
  return found || null
}

// Reconnait le format d'apres la ligne d'en-tete. Renvoie 'spa', 'booker' ou null.
export function detecterFormat(text) {
  const premiere = (text || '').replace(/^﻿/, '').split(/\r?\n/)[0] || ''
  const h = premiere.split(';').map(normaliserLabel)
  if (h.includes('N° LIGNE') && h.includes('TYPE DE GRATUITE')) return 'spa'
  if (h.some(x => x.includes('CATEGORY'))) return 'booker'
  return null
}

// ---------------------------------------------------------------- Booker
// Logique reprise telle quelle de l'ancien ImportCSV.js (aucun changement de comportement).
export function parseBooker(text) {
  const lines = text.split('\n').filter(l => l.trim())
  const header = lines[0].split(';')

  const idx = {
    date: header.findIndex(h => h.trim().toLowerCase().includes('date')),
    category: header.findIndex(h => h.trim().toLowerCase().includes('itemcategory') || h.trim().toLowerCase().includes('category')),
    subtotal: header.findIndex(h => h.trim().toLowerCase().includes('subtotal')),
    accueil: header.findIndex(h => h.trim().toLowerCase().includes('orderclosedby') || h.trim().toLowerCase().includes('closedby')),
    superviseur: header.findIndex(h => h.trim().toLowerCase() === 'superviseur'),
  }

  const rows = lines.slice(1).map(l => {
    const cols = l.split(';')
    const firstName = normaliserLabel(cols[2])
    const lastName = normaliserLabel(cols[3])
    // Type de gratuit depuis CustomerLastName (ELEVE, DEPANNAGE) ou CustomerFirstName (STAFF, SUPERVISEUR)
    let gratuitType = null
    if (lastName === 'ELEVE') gratuitType = 'ELEVE'
    else if (lastName === 'CAMPUS' || firstName === 'STAFF') gratuitType = 'VIP'
    else if (lastName === 'DEPANNAGE' || firstName === 'SUPERVISEUR') gratuitType = 'SUP'
    else if (lastName === 'VIP') gratuitType = 'VIP'
    else if (lastName === 'JPO') gratuitType = 'JPO'
    return {
      date: parseDate(cols[idx.date]),
      category: (cols[idx.category] || '').trim().toLowerCase(),
      subtotal: parseMontant(cols[idx.subtotal]),
      accueil: (cols[idx.accueil] || '').trim(),
      superviseur: (cols[idx.superviseur] || '').trim(),
      gratuitType,
    }
  }).filter(r => r.date && (
    CATEGORIES_SPA.includes(r.category) || r.category === CATEGORIE_HYDRO
  ))

  return {
    rows: rows.map(r => ({
      date: r.date,
      pole: r.category === CATEGORIE_HYDRO ? 'HYDRO' : 'SPA',
      subtotal: r.subtotal,
      accueil: r.accueil,
      superviseur: r.superviseur,
      gratuitType: r.gratuitType,
    })),
    avertissements: [],
    periode: null,
  }
}

// ------------------------------------------------------------- App spa
// Lecteur CSV minimal (separateur ;, guillemets doubles, BOM et CRLF toleres).
export function lireCsv(text) {
  const src = (text || '').replace(/^﻿/, '')
  const lignes = []
  let ligne = []
  let champ = ''
  let guillemets = false
  for (let i = 0; i < src.length; i++) {
    const c = src[i]
    if (guillemets) {
      if (c === '"' && src[i + 1] === '"') { champ += '"'; i++ }
      else if (c === '"') guillemets = false
      else champ += c
    } else if (c === '"') guillemets = true
    else if (c === ';') { ligne.push(champ); champ = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++
      ligne.push(champ); champ = ''
      if (ligne.some(x => x.trim() !== '')) lignes.push(ligne)
      ligne = []
    } else champ += c
  }
  ligne.push(champ)
  if (ligne.some(x => x.trim() !== '')) lignes.push(ligne)
  return lignes
}

const COLONNES_SPA = {
  date: 'DATE',
  dateSoin: 'DATE DU SOIN',
  type: 'TYPE',
  total: 'TOTAL',
  paiement: 'PAIEMENT',
  gratuite: 'TYPE DE GRATUITE',
  superviseur: 'SUPERVISEUR',
  ligne: 'N° LIGNE',
}

// Une ligne de commande payee en plusieurs reglements apparait plusieurs fois dans le CSV
// (meme N° ligne). On regroupe par N° ligne : 1 soin, montant = somme des Total.
export function parseSpa(text) {
  const table = lireCsv(text)
  if (table.length === 0) throw new Error('Fichier vide.')
  const header = table[0].map(normaliserLabel)
  const idx = {}
  const manquantes = []
  for (const [cle, nom] of Object.entries(COLONNES_SPA)) {
    idx[cle] = header.indexOf(nom)
    if (idx[cle] === -1) manquantes.push(nom.toLowerCase())
  }
  if (manquantes.length > 0) throw new Error(`Colonnes manquantes : ${manquantes.join(', ')}.`)

  const avertissements = []
  const lignes = new Map()   // N° ligne -> lignes CSV
  const ignores = {}         // type -> { n, montant }
  let sansNumeroLigne = 0
  let min = null, max = null

  table.slice(1).forEach((cols, i) => {
    const dp = parseDate(cols[idx.date])
    if (dp) { if (!min || dp < min) min = dp; if (!max || dp > max) max = dp }
    const type = normaliserNom(cols[idx.type])
    const estSoin = CATEGORIES_SPA.includes(type) || type === CATEGORIE_HYDRO
    if (!estSoin) {
      const t = (cols[idx.type] || '').trim() || '(vide)'
      if (!ignores[t]) ignores[t] = { n: 0, montant: 0 }
      ignores[t].n++
      ignores[t].montant += parseMontant(cols[idx.total])
      return
    }
    let cle = (cols[idx.ligne] || '').trim()
    if (!cle) { cle = `sans-numero-${i}`; sansNumeroLigne++ }
    if (!lignes.has(cle)) lignes.set(cle, [])
    lignes.get(cle).push({ cols, type })
  })

  for (const [t, v] of Object.entries(ignores)) {
    if (!TYPES_VENTE_IGNORES.includes(normaliserNom(t))) {
      avertissements.push(`Type « ${t} » ignoré (${v.n} ligne${v.n > 1 ? 's' : ''}, ${v.montant.toFixed(2)} €) : libellé non reconnu comme soin.`)
    }
  }
  if (sansNumeroLigne > 0) avertissements.push(`${sansNumeroLigne} ligne(s) de soin sans N° ligne : comptées chacune comme un soin.`)

  let nonPrecise = 0, dateRepli = 0
  const rows = []
  for (const parts of lignes.values()) {
    const premiere = parts[0].cols
    const total = parts.reduce((s, p) => s + parseMontant(p.cols[idx.total]), 0)
    const gratuit = parts.some(p => normaliserLabel(p.cols[idx.paiement]) === 'GRATUIT')
    let date = parseDate(premiere[idx.dateSoin])
    if (!date) { date = parseDate(premiere[idx.date]); dateRepli++ }
    if (!date) continue
    let gratuitType = null
    if (gratuit) {
      gratuitType = normaliserLabel(premiere[idx.gratuite])
      if (!gratuitType || gratuitType === 'NON PRECISE') { gratuitType = 'NON_PRECISE'; nonPrecise++ }
    }
    const superviseur = (parts.map(p => (p.cols[idx.superviseur] || '').trim()).find(s => s) || '')
    rows.push({
      date,
      pole: parts[0].type === CATEGORIE_HYDRO ? 'HYDRO' : 'SPA',
      subtotal: gratuit ? 0 : total,
      accueil: '',
      superviseur,
      gratuitType,
    })
  }
  if (nonPrecise > 0) avertissements.push(`${nonPrecise} gratuité(s) sans type précisé (« Non précisé ») : rattachées au premier tarif gratuit du pôle.`)
  if (dateRepli > 0) avertissements.push(`${dateRepli} soin(s) sans « Date du soin » : date de paiement utilisée.`)

  return { rows, avertissements, periode: min ? { min, max } : null }
}

// ------------------------------------------------------------ Agregation
// Regroupe par (date, pole), compte payants / gratuits, somme le CA et rattache chaque soin a un tarif.
// Logique reprise de l'ancien ImportCSV.js.
// options.superviseurPremierNonVide : prend le premier superviseur renseigne du groupe
// (format spa). Sinon, superviseur de la premiere ligne (comportement Booker historique).
export function agreger(rows, tarifs, options = {}) {
  const parDatePole = {}
  for (const row of rows) {
    const pole = row.pole
    const key = `${row.date}_${pole}`
    if (!parDatePole[key]) {
      parDatePole[key] = {
        date: row.date,
        pole,
        accueilNom: row.accueil,
        superviseurNom: row.superviseur,
        superviseursVus: [],
        payants: 0, gratuits: 0,
        valorisation: 0, jposSansValorisation: 0,
        ca: 0, soins: {},
      }
    }
    const d = parDatePole[key]
    // Garder le premier accueil trouvé pour la date (commun à tous les pôles)
    if (!d.accueilNom && row.accueil) d.accueilNom = row.accueil
    if (options.superviseurPremierNonVide && !d.superviseurNom && row.superviseur) d.superviseurNom = row.superviseur
    if (row.superviseur && !d.superviseursVus.includes(row.superviseur)) d.superviseursVus.push(row.superviseur)
    const gratuit = !!row.gratuitType

    if (gratuit) {
      d.gratuits++
      const tarifsGratuits = tarifs?.filter(t => t.pole === pole && t.prix === 0) || []
      const motsCles = MOTS_CLES_GRATUIT[row.gratuitType] || [row.gratuitType || 'VIP']
      const correspond = t => motsCles.some(mot => normaliserLabel(t.label).includes(mot))
      let tarifGratuit
      if (row.gratuitType === 'JPO') {
        // Portes ouvertes : gratuit pour le client, valorise pour le superviseur via prix_reference
        // (meme règle que la saisie manuelle). On cherche d'abord parmi les tarifs gratuits valorises.
        const valorises = tarifsGratuits.filter(t => Number(t.prix_reference) > 0)
        tarifGratuit = valorises.find(correspond) || (valorises.length === 1 ? valorises[0] : undefined)
        if (tarifGratuit) d.valorisation += Number(tarifGratuit.prix_reference)
        else d.jposSansValorisation++
      }
      if (!tarifGratuit) tarifGratuit = tarifsGratuits.find(correspond)
      // Repli : aucun libellé ne correspond mais le pôle a un tarif gratuit -> on le prend
      // pour que le massage soit compté (mieux vaut compté que perdu)
      if (!tarifGratuit) tarifGratuit = tarifsGratuits[0]
      if (tarifGratuit) d.soins[tarifGratuit.id] = (d.soins[tarifGratuit.id] || 0) + 1
    } else {
      d.payants++; d.ca += row.subtotal
      const tarifMatch = tarifs?.filter(t => t.pole === pole && t.prix > 0)
        .sort((a, b) => Math.abs(a.prix - row.subtotal) - Math.abs(b.prix - row.subtotal))[0]
      if (tarifMatch) d.soins[tarifMatch.id] = (d.soins[tarifMatch.id] || 0) + 1
    }
  }
  return Object.values(parDatePole)
    .sort((a, b) => a.date.localeCompare(b.date) || a.pole.localeCompare(b.pole))
}
